<app-nav></app-nav>

<main class="privacy-page">
  <!-- ─── Hero ─────────────────────────────────────────────────────── -->
  <section class="legal-hero brand-section">
    <div class="hero-orb hero-orb-blue"></div>
    <div class="hero-orb hero-orb-green"></div>

    <div class="legal-hero-inner">
      <span class="brand-eyebrow">Legal</span>
      <h1>Privacy Policy</h1>
      <p class="brand-lede">
        We keep this short and plain because you actually need to be able to read it.
      </p>
      <p class="last-updated">Last Updated: {{ lastUpdated }}</p>
    </div>
  </section>

  <!-- ─── Content ───────────────────────────────────────────────────── -->
  <section class="legal-body brand-section">
    <div class="legal-grid">

      <!-- Sticky nav -->
      <nav class="legal-toc" aria-label="Section navigation">
        <p class="toc-label">On this page</p>
        <ol class="toc-list">
          <li><a [routerLink]="['/privacy']" fragment="introduction">Introduction</a></li>
          <li><a [routerLink]="['/privacy']" fragment="what-we-collect">What we collect</a></li>
          <li><a [routerLink]="['/privacy']" fragment="location">Precise location</a></li>
          <li><a [routerLink]="['/privacy']" fragment="how-we-use">How we use it</a></li>
          <li><a [routerLink]="['/privacy']" fragment="legal-bases">Legal bases</a></li>
          <li><a [routerLink]="['/privacy']" fragment="sharing">Sharing &amp; disclosure</a></li>
          <li><a [routerLink]="['/privacy']" fragment="retention">Retention &amp; deletion</a></li>
          <li><a [routerLink]="['/privacy']" fragment="security">Security</a></li>
          <li><a [routerLink]="['/privacy']" fragment="your-rights">Your rights</a></li>
          <li><a [routerLink]="['/privacy']" fragment="us-state">US state notices</a></li>
          <li><a [routerLink]="['/privacy']" fragment="eu-uk">EU &amp; UK notices</a></li>
          <li><a [routerLink]="['/privacy']" fragment="children">Children's privacy</a></li>
          <li><a [routerLink]="['/privacy']" fragment="third-parties">Third-party services</a></li>
          <li><a [routerLink]="['/privacy']" fragment="transfers">International transfers</a></li>
          <li><a [routerLink]="['/privacy']" fragment="changes">Policy changes</a></li>
          <li><a [routerLink]="['/privacy']" fragment="contact">Contact us</a></li>
        </ol>
      </nav>

      <!-- Body -->
      <div class="legal-content">

        <section id="introduction" class="legal-section">
          <h2>1. Introduction</h2>
          <p>
            JCC Development LLC ("we," "our," or "us") operates the Skip To My Loo mobile application
            and the stml.app website (together, the "App"). This Privacy Policy explains what
            information we collect, how we use and share it, how long we keep it, and the choices
            and rights you have.
          </p>
          <p>
            If you do not agree with this policy, please do not use the App. Capitalized terms not
            defined here have the meaning given in our <a routerLink="/terms">Terms of Service</a>.
          </p>
        </section>

        <section id="what-we-collect" class="legal-section">
          <h2>2. Information We Collect</h2>

          <h3>Information You Give Us</h3>
          <p>We collect information you provide directly when you:</p>
          <ul>
            <li><strong>Create an account:</strong> your name, email address, and password (stored only as a salted hash). If you sign in with Apple or Google, we receive your name and email address from that provider.</li>
            <li><strong>Contribute to the community:</strong> restroom locations and descriptions you submit, and the reviews, ratings, and attribute answers you write. These are public.</li>
            <li><strong>Save favorites:</strong> the restrooms you favorite and any private notes you add to them.</li>
            <li><strong>Play games:</strong> your scores, difficulty, and when you played.</li>
            <li><strong>Contact us:</strong> whatever you include in a support or contact-form message.</li>
            <li><strong>Report a problem:</strong> the report type and description you submit about a restroom or review.</li>
          </ul>

          <h3>Information Collected Automatically</h3>
          <ul>
            <li><strong>Activity:</strong> which features you use, searches you run, restrooms you view, badges and XP you earn, and your app preferences (for example theme and haptics), so they follow you between devices.</li>
            <li><strong>Device and diagnostics:</strong> device model, operating system version, app version, language, and a random installation identifier. When the App crashes or hits an error, our crash-reporting provider (Sentry) receives a diagnostic report that includes your account identifier (never your email or name) so we can tell whether a problem affects one person or many.</li>
            <li><strong>Advertising identifiers:</strong> on the free tier, the App shows ads delivered by Google AdMob. We request <strong>non-personalized ads only</strong>, which means AdMob does not use your advertising identifier to build an interest profile for our ads. AdMob may still process your IP address, device information, and coarse location to serve, cap, and measure ads and to detect fraud.</li>
          </ul>

          <h3>Voice Dictation for Reviews</h3>
          <p>
            When you use the optional voice button while writing a review, your spoken words are
            transcribed entirely on your device using your phone's built-in speech recognition
            (Apple's Speech framework on iOS, Android's <code>SpeechRecognizer</code> on Android).
            The audio never leaves your device, is never uploaded to our servers, and is never
            stored. Only the resulting text, which you can edit before submitting, is sent to us
            as part of the review you choose to share. Because transcription happens inside your
            device's operating system, Apple's and Google's own privacy terms govern how that
            on-device speech feature works.
          </p>
        </section>

        <section id="location" class="legal-section">
          <h2>3. Precise Location Data</h2>
          <p>
            Finding a restroom near you is the point of the App, so with your permission we use
            your device's precise GPS location. Under several privacy laws, including California's
            CPRA, precise geolocation is treated as <strong>sensitive personal information</strong>.
            Here is exactly how we handle it:
          </p>
          <ul>
            <li><strong>Used in real time, not stored as a history.</strong> Your current coordinates are sent to our servers only to answer the request in front of you (for example "restrooms within two miles"). We do not keep a log of where you have been, and we do not store your location with timestamps.</li>
            <li><strong>Stored only when you choose to contribute.</strong> When you submit a new restroom, the coordinates of that restroom (not of you) are saved as part of the public listing. Your contribution history is linked to your account so it can appear on your profile map.</li>
            <li><strong>Never sold or shared for advertising.</strong> We do not sell precise location data and do not use it for targeted advertising.</li>
          </ul>
          <p>
            <strong>You can revoke location access at any time.</strong> On iOS, open Settings, choose
            Skip To My Loo, then Location. On Android, open Settings, choose Apps, Skip To My Loo,
            then Permissions. The App keeps working without location, but you will need to search
            by place name instead of seeing restrooms around you. Limiting the use of your sensitive
            personal information in this way is your right, and we will not treat you differently
            for exercising it.
          </p>
        </section>

        <section id="how-we-use" class="legal-section">
          <h2>4. How We Use Your Information</h2>
          <p>We use the information we collect to:</p>
          <ul>
            <li>Provide, maintain, and improve the App</li>
            <li>Show you nearby restrooms based on your current location</li>
            <li>Publish the reviews, ratings, and restroom listings you contribute</li>
            <li>Generate short AI-written summaries of what reviewers say about a restroom (see Section 13)</li>
            <li>Verify new restroom submissions for accuracy, using automated checks against map data and human moderators</li>
            <li>Track your contribution history, XP, levels, badges, and game scores, and run leaderboards and events</li>
            <li>Manage your STML Premium subscription</li>
            <li>Show ads on the free tier so the App can stay free</li>
            <li>Respond to your messages and provide support</li>
            <li>Send you transactional emails, such as email verification and password resets</li>
            <li>Detect, prevent, and respond to fraud, abuse, security incidents, and crashes</li>
            <li>Understand how the App is used so we can make it better</li>
            <li>Comply with legal obligations</li>
          </ul>
          <p>We do not send marketing email, and we do not use your information for profiling that has legal or similarly significant effects on you.</p>
        </section>

        <section id="legal-bases" class="legal-section">
          <h2>5. Legal Bases for Processing</h2>
          <p>
            Where the GDPR or UK GDPR applies, we rely on the following legal bases:
          </p>
          <ul>
            <li><strong>Performance of a contract:</strong> creating and running your account, publishing your contributions, tracking game progress, and managing your subscription.</li>
            <li><strong>Consent:</strong> accessing your precise location and showing ads. You can withdraw consent at any time in your device settings, and withdrawing it does not affect processing that happened before.</li>
            <li><strong>Legitimate interests:</strong> keeping the App secure, preventing fraud and abuse, fixing crashes, understanding aggregate usage, and moderating content. We balance these interests against your rights and use the least data needed.</li>
            <li><strong>Legal obligation:</strong> responding to lawful requests and keeping records we are required to keep.</li>
          </ul>
        </section>

        <section id="sharing" class="legal-section">
          <h2>6. Information Sharing and Disclosure</h2>
          <p>
            <strong>We do not sell your personal information, and we do not share it for
            cross-context behavioral advertising.</strong> We share information only in these
            circumstances:
          </p>
          <ul>
            <li><strong>Public content:</strong> restroom listings, reviews, ratings, your display name, level, and badges are visible to other users of the App.</li>
            <li><strong>Service providers:</strong> companies that process data on our behalf and only on our instructions, such as cloud hosting, email delivery, crash reporting (Sentry), subscription management (RevenueCat), AI summarization (Anthropic), map services (Google Maps), and spam prevention (Cloudflare). Section 13 lists them.</li>
            <li><strong>Ad network:</strong> Google AdMob receives the device signals described in Section 2 to serve non-personalized ads on the free tier.</li>
            <li><strong>Legal reasons:</strong> when required by law, subpoena, or court order, or when we believe disclosure is necessary to protect the rights, property, or safety of our users, the public, or JCC Development LLC.</li>
            <li><strong>Business transfers:</strong> if we are involved in a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction. We will notify you before your information becomes subject to a different privacy policy.</li>
          </ul>
        </section>

        <section id="retention" class="legal-section">
          <h2>7. Data Retention and Account Deletion</h2>

          <h3>How Long We Keep Information</h3>
          <ul>
            <li><strong>Account details, favorites, preferences, badges, and game scores:</strong> for as long as your account is active.</li>
            <li><strong>Reviews, ratings, and restroom submissions:</strong> for as long as your account is active. Approved restroom listings are community map data and are kept after account deletion, without your name attached.</li>
            <li><strong>Precise location:</strong> not retained. Coordinates are used to answer your request and then discarded (see Section 3).</li>
            <li><strong>Support and contact-form messages:</strong> up to 24 months after the conversation ends, so we can follow up on related issues.</li>
            <li><strong>Crash and diagnostic reports:</strong> 90 days.</li>
            <li><strong>Server logs:</strong> up to 30 days for security and troubleshooting.</li>
            <li><strong>Moderation and audit records:</strong> records of moderation decisions and account enforcement are kept for up to 3 years to resolve disputes and comply with our legal obligations.</li>
          </ul>

          <h3>What Happens When You Delete Your Account</h3>
          <p>
            You can permanently delete your account from Settings in the App (Settings, Account,
            Delete account) or by emailing <a href="mailto:privacy@stml.app">privacy&#64;stml.app</a>
            from the address on your account. When you do:
          </p>
          <ul>
            <li>Your profile, email address, name, password hash, sign-in identifiers, preferences, favorites, badges, XP, game scores, tournament results, reports you filed, and all of your sessions are deleted immediately.</li>
            <li>Your reviews and ratings are deleted, and each affected restroom's average rating is recalculated without them.</li>
            <li>Restroom listings you submitted remain on the map so the community keeps the benefit of them, but they are no longer linked to you in any way.</li>
            <li>Copies in our encrypted backups are overwritten on the normal rotation schedule, within 30 days.</li>
            <li>Deleting your account does not cancel an active STML Premium subscription. Cancel it in your App Store or Google Play subscription settings.</li>
          </ul>
          <p>Account deletion is permanent and cannot be reversed.</p>
        </section>

        <section id="security" class="legal-section">
          <h2>8. Data Security</h2>
          <p>
            We use appropriate technical and organizational measures to protect your information,
            including encryption in transit (TLS), encrypted storage, hashed passwords, short-lived
            access tokens, and access controls that limit who at JCC Development LLC can see
            personal data. No method of transmission or storage is completely secure, so we cannot
            guarantee absolute security. If we learn of a breach affecting your personal
            information, we will notify you as required by law.
          </p>
        </section>

        <section id="your-rights" class="legal-section">
          <h2>9. Your Privacy Rights</h2>
          <p>Depending on where you live, you may have the right to:</p>
          <ul>
            <li><strong>Access:</strong> receive a copy of the personal information we hold about you.</li>
            <li><strong>Correction:</strong> update inaccurate information. You can edit your name in the App at any time.</li>
            <li><strong>Deletion:</strong> delete your account and personal information (see Section 7).</li>
            <li><strong>Portability:</strong> receive your data in a structured, machine-readable format.</li>
            <li><strong>Limit sensitive data:</strong> restrict our use of your precise location by turning off location access in your device settings.</li>
            <li><strong>Object or restrict:</strong> object to processing based on legitimate interests, or ask us to restrict processing while a request is reviewed.</li>
            <li><strong>Withdraw consent:</strong> at any time, for processing based on consent.</li>
            <li><strong>Non-discrimination:</strong> we will not deny you the App, charge you a different price, or provide a different level of service because you exercised any of these rights.</li>
          </ul>
          <p>
            To exercise a right, email <a href="mailto:privacy@stml.app">privacy&#64;stml.app</a>
            from the address on your account, or use the <a routerLink="/contact">contact form</a>.
            We will verify your identity by confirming you control the account's email address, and
            we respond within 45 days (or the shorter period required by your local law). You may
            designate an authorized agent to make a request on your behalf; we will ask the agent
            for proof of your authorization. If we decline a request, you can appeal by replying to
            our decision, and we will tell you how to reach your local regulator.
          </p>
        </section>

        <section id="us-state" class="legal-section">
          <h2>10. Notice to Residents of California and Other US States</h2>
          <p>
            This section applies to residents of California, Colorado, Connecticut, Virginia,
            Utah, Texas, Oregon, Montana, and other states with comprehensive privacy laws, to the
            extent those laws apply to us.
          </p>
          <ul>
            <li><strong>Categories collected:</strong> identifiers (name, email, account identifier, device identifiers), customer records, commercial information (subscription status), internet activity (feature use, crash reports), precise geolocation (sensitive), and user-generated content. Sources: you, your device, Apple or Google sign-in, and our subscription provider. Purposes: those listed in Section 4.</li>
            <li><strong>No sale or sharing:</strong> we do not sell personal information and do not share it for cross-context behavioral advertising, and we have not done so in the preceding 12 months. We do not knowingly sell or share the personal information of anyone under 16.</li>
            <li><strong>Sensitive personal information:</strong> we use precise geolocation only to provide the service you request, and you can limit it as described in Section 3.</li>
            <li><strong>Shine the Light:</strong> we do not disclose personal information to third parties for their own direct marketing purposes.</li>
            <li><strong>Global Privacy Control:</strong> because we do not sell or share personal information, there is nothing to opt out of, but we honor browser opt-out preference signals as a request to opt out where required.</li>
            <li><strong>Your rights</strong> are described in Section 9 and include the right to know, delete, correct, and limit, and to appeal a denied request, without discrimination.</li>
          </ul>
        </section>

        <section id="eu-uk" class="legal-section">
          <h2>11. Notice to Users in the European Economic Area, United Kingdom, and Switzerland</h2>
          <p>
            JCC Development LLC is the controller of your personal information. Our legal bases
            are set out in Section 5, and your rights in Section 9. You also have the right to
            lodge a complaint with your local supervisory authority; a list of EU authorities is
            available from the European Data Protection Board, and UK users can contact the
            Information Commissioner's Office. We would appreciate the chance to address your
            concern first at <a href="mailto:privacy@stml.app">privacy&#64;stml.app</a>.
          </p>
          <p>
            Our servers are in the United States. See Section 14 for the safeguards we use when we
            transfer your information outside your region.
          </p>
        </section>

        <section id="children" class="legal-section">
          <h2>12. Children's Privacy</h2>
          <p>
            The App is not intended for children under 13, and we do not knowingly collect personal
            information from children under 13. Users between 13 and the age of majority may use
            the App only with a parent or guardian's permission. If you believe a child under 13 has
            created an account, contact us at <a href="mailto:privacy@stml.app">privacy&#64;stml.app</a>
            and we will delete it promptly.
          </p>
        </section>

        <section id="third-parties" class="legal-section">
          <h2>13. Third-Party Services</h2>
          <p>
            The App relies on the services below. Each processes data under its own privacy
            policy, which we encourage you to read.
          </p>

          <h3>Google Maps</h3>
          <p>
            We use Google Maps to display restroom locations and surrounding geography, and to
            help verify new submissions against known places. Google may collect device and usage
            data through its Maps SDK. See
            <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google's Privacy Policy</a>.
          </p>

          <h3>Google AdMob (Advertising)</h3>
          <p>
            On the free tier, ads are served by Google AdMob. We configure AdMob to request
            non-personalized ads only. AdMob may process your IP address, device information, and
            coarse location for ad delivery, frequency capping, measurement, and fraud prevention.
            STML Premium removes ads. See
            <a href="https://policies.google.com/technologies/ads" target="_blank" rel="noopener noreferrer">how Google uses information from ads</a>.
          </p>

          <h3>Anthropic (AI Summaries and Verification)</h3>
          <p>
            We use Anthropic's Claude API for two things: generating a short, aggregated summary of
            what reviewers say about a restroom, and helping check whether a newly submitted
            restroom looks legitimate. Only review text and listing details are sent; your name,
            email, and account identifiers are not. Under Anthropic's commercial API terms, data
            sent through the API is not used to train Anthropic's models. Because reviews are
            public and are processed by an AI system, <strong>please do not include personal or
            sensitive information about yourself or anyone else in a review</strong>. See
            <a href="https://www.anthropic.com/legal/privacy" target="_blank" rel="noopener noreferrer">Anthropic's Privacy Policy</a>.
          </p>

          <h3>Sentry (Crash Reporting)</h3>
          <p>
            When the App crashes or encounters an error, a diagnostic report is sent to Sentry. It
            includes device and app version details, the sequence of screens that led to the error,
            and, if you are signed in, your account identifier only. We do not send your email,
            name, or IP address to Sentry. Reports are deleted after 90 days. See
            <a href="https://sentry.io/privacy/" target="_blank" rel="noopener noreferrer">Sentry's Privacy Policy</a>.
          </p>

          <h3>Cloudflare Turnstile</h3>
          <p>
            Our website's contact form uses Cloudflare Turnstile to block spam. Turnstile collects
            minimal device and browser signals strictly to verify that a submission comes from a
            person. See
            <a href="https://www.cloudflare.com/privacypolicy/" target="_blank" rel="noopener noreferrer">Cloudflare's Privacy Policy</a>.
          </p>

          <h3>Apple App Store, Google Play, and RevenueCat (Payments)</h3>
          <p>
            STML Premium subscriptions are purchased in the App and billed through your Apple App
            Store or Google Play account. Apple or Google processes the payment directly; we never
            receive or store your card details. RevenueCat, our subscription platform, shares
            limited purchase information with us (such as subscription status and renewal dates) so
            we can unlock Premium on your account. See
            <a href="https://www.apple.com/legal/privacy/" target="_blank" rel="noopener noreferrer">Apple's Privacy Policy</a>,
            <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google's Privacy Policy</a>, and
            <a href="https://www.revenuecat.com/privacy/" target="_blank" rel="noopener noreferrer">RevenueCat's Privacy Policy</a>.
          </p>

          <h3>Apple and Google Sign-In</h3>
          <p>
            If you sign in with Apple or Google, that provider authenticates you and shares a
            limited profile (name and email address) with us. Their privacy policies govern what
            they collect on their end.
          </p>
        </section>

        <section id="transfers" class="legal-section">
          <h2>14. International Data Transfers</h2>
          <p>
            We are based in the United States and store information on servers in the United
            States. If you use the App from outside the United States, your information is
            transferred to, stored, and processed there, where privacy laws may differ from those
            of your country. Where required, we rely on recognized transfer mechanisms such as the
            European Commission's Standard Contractual Clauses (and the UK addendum), or an
            adequacy decision, and we require our service providers to protect your information to
            a comparable standard.
          </p>
        </section>

        <section id="changes" class="legal-section">
          <h2>15. Changes to This Privacy Policy</h2>
          <p>
            We may update this policy from time to time. We will post the updated policy on this
            page and change the "Last Updated" date, and for material changes we will also notify
            you in the App or by email before they take effect.
          </p>
        </section>

        <section id="contact" class="legal-section">
          <h2>16. Contact Us</h2>
          <p>If you have questions, concerns, or requests about this policy or your information, contact us at:</p>
          <div class="contact-block">
            <strong>JCC Development LLC</strong>
            <a href="mailto:privacy@stml.app">privacy&#64;stml.app</a>
            <a routerLink="/contact">Contact form</a>
            <!-- TODO: add the LLC's mailing address here once confirmed. -->
          </div>
        </section>

      </div>
    </div>
  </section>
</main>

<app-footer></app-footer>
